import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

/** Short-throw swipe with a worded plate, revealed before anything commits.
 *
 *  From the interaction contract: swipe left completes, swipe right migrates to
 *  tomorrow, the threshold is 64px, and a release short of it snaps back having
 *  done nothing. Nothing destructive lives on a swipe — cancel and delete are
 *  sheet-only and take a worded confirm, so the two gestures here are the two
 *  that a toast can undo.
 *
 *  The plate carries a **word**, not just a colour or an icon: `x DONE` and
 *  `> TOMORROW`. Committing on release rather than on crossing the threshold is
 *  what makes the throw recoverable — you can cross it and come back.
 *
 *  Pointer events, so this works under a mouse as well as a finger; the capture
 *  harness drives it that way. Every gesture still has a sheet equivalent, so
 *  nothing here is the only route to an action.
 */
export const SWIPE_THRESHOLD = 64;

export function SwipeRow({
  children,
  onLeft,
  onRight,
  leftLabel = 'x DONE',
  rightLabel = '> TOMORROW',
  enabled = true,
}: {
  children: ComponentChildren;
  /** Swipe left. Omit to disable that direction for this row. */
  onLeft?: () => void;
  onRight?: () => void;
  leftLabel?: string;
  rightLabel?: string;
  enabled?: boolean;
}) {
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number; id: number } | null>(null);
  // Set once a drag is clearly horizontal, so a vertical scroll is never
  // hijacked by a row.
  const horizontal = useRef(false);

  const armed = Math.abs(dx) >= SWIPE_THRESHOLD;
  const towards = dx < 0 ? 'left' : 'right';
  const available = towards === 'left' ? Boolean(onLeft) : Boolean(onRight);

  const reset = () => {
    start.current = null;
    horizontal.current = false;
    setDx(0);
  };

  if (!enabled || (!onLeft && !onRight)) return <>{children}</>;

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        // Vertical panning stays the browser's; horizontal is ours.
        touchAction: 'pan-y',
        // iOS starts a text selection from a drag that begins on a word, and
        // shows the selection callout on a long press — both of which fight a
        // row you are trying to throw. Neither shows up under Chrome's touch
        // emulation, so this is a guard the harness cannot check.
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {/* The plate sits behind the row and only says anything once the throw is
          committed-if-released. Before that it is silent, so a stray drag does
          not announce an action. */}
      {dx !== 0 && available ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: towards === 'left' ? 'flex-end' : 'flex-start',
            padding: '0 var(--bujo-space-3)',
            background: armed ? 'var(--bujo-select-ground)' : 'transparent',
            font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
            letterSpacing: 'var(--bujo-ls-label)',
            color: armed ? 'var(--bujo-accent)' : 'var(--bujo-text-meta)',
          }}
        >
          {towards === 'left' ? leftLabel : rightLabel}
        </div>
      ) : null}

      <div
        onPointerDown={(event) => {
          // Primary button or touch only; a right-click is not a swipe.
          if (event.button !== 0) return;
          start.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
          // Capture is taken in `pointermove`, once a drag is real — not here.
          // Capturing on down retargets the `pointerup` to this div, so the
          // browser dispatches `click` here instead of on the row's button and
          // a plain tap stopped selecting the entry entirely.
        }}
        onPointerMove={(event) => {
          const from = start.current;
          if (!from || from.id !== event.pointerId) return;
          const moved = event.clientX - from.x;
          const lifted = Math.abs(event.clientY - from.y);
          if (!horizontal.current) {
            // Vertical intent wins: a list has to stay scrollable.
            if (lifted > Math.abs(moved) && lifted > 8) return reset();
            if (Math.abs(moved) < 8) return;
            horizontal.current = true;
            // Now that this is a drag and not a tap, keep the pointer: the row
            // is about to translate away from the finger, and without capture
            // that fires `pointerleave` and resets mid-throw.
            (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
          }
          const direction = moved < 0 ? onLeft : onRight;
          setDx(direction ? moved : 0);
        }}
        onPointerUp={() => {
          const throwLeft = dx <= -SWIPE_THRESHOLD && onLeft;
          const throwRight = dx >= SWIPE_THRESHOLD && onRight;
          reset();
          // Committed on release, not on crossing — so a throw can be taken back.
          if (throwLeft) onLeft?.();
          else if (throwRight) onRight?.();
        }}
        onPointerCancel={reset}
        style={{
          transform: `translateX(${dx}px)`,
          // No transition while dragging: the row has to track the finger.
          transition: dx === 0 ? 'transform var(--bujo-dur-sheet) var(--bujo-ease)' : 'none',
          background: 'var(--bujo-surface-page)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
